import React, { useState } from 'react';
import clsx from 'clsx';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';

import {
  Contact,
  Supplier,
  SupplierCompanyDetailRequest
} from 'src/types/supplier';
import { slices, useAppDispatch } from 'src/redux';
import { localize } from 'src/constants';
import { usePermissions, useSnackBar } from 'src/hooks';
import { LoaderBar } from 'src/components';
import { containsAlphabetsWithSpacesAndDashes } from 'src/utils';
import SupplierDialogForm from '../components/SupplierDialogForm';

interface Props {
  supplier?: Supplier | null;
  className?: string;
}

const { actions: supplierActions } = slices.supplier;

const useStyles = makeStyles(() => ({
  root: {},
  avatar: {
    height: 100,
    width: 100
  }
}));

const SupplierDetailView = ({ className, supplier, ...rest }: Props) => {
  const classes = useStyles();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const { canEditSupplier, canDeleteSupplier } = usePermissions();

  const [showEdit, setShowEdit] = useState(false);
  const [supplierDetail, setSupplierDetail] = useState(supplier);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSaveDetailPress = async (
    companyName: string,
    companyDetails: SupplierCompanyDetailRequest[],
    contacts: Contact[] | null
  ) => {
    if (!id || !canEditSupplier) {
      return;
    }

    if (
      !companyName ||
      !containsAlphabetsWithSpacesAndDashes(companyName)
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide proper Company name'
      });
      return;
    }

    const response = unwrapResult(
      await dispatch(
        supplierActions.updateSupplierThunk({
          id: +id,
          company_name: companyName,
          company_details: companyDetails,
          contacts
        })
      )
    );

    if (response?.success && response?.message) {
      snackBar.show({ severity: 'success', message: response.message });
      setSupplierDetail(response.originalData.supplier);
      setShowEdit(false);
    } else {
      snackBar.show({
        severity: 'error',
        message: response?.message ? response.message : localize.ERR_API_UNKNOWN
      });
    }
  };

  const onDeleteSupplier = async () => {
    if (!id || !canDeleteSupplier) {
      return;
    }
    setIsLoading(true);
    const deleteSupplierResponse = unwrapResult(
      await dispatch(supplierActions.supplierDeleteThunk(+id))
    );
    if (deleteSupplierResponse?.success && deleteSupplierResponse?.message) {
      setIsLoading(false);
      // eslint-disable-next-line no-alert
      navigate('/app/suppliers');
    }
    // eslint-disable-next-line no-alert
    // alert('Deletion of supplier is not allowed.');
  };

  return (
    <Box mt={2}>
      <Card className={clsx(classes.root, className)} {...rest}>
        <LoaderBar isLoading={isLoading} />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item lg={8} md={8} xs={8}>
              <Box>
                <Typography
                  style={{ marginTop: 10 }}
                  color="textPrimary"
                  gutterBottom
                  variant="h3"
                >
                  {supplierDetail?.name ? supplierDetail?.name : supplier?.name}
                </Typography>
                <Typography color="textSecondary" variant="h6">
                  {supplierDetail?.company_name || supplier?.company_name
                    ? supplierDetail?.company_name
                      ? supplierDetail?.company_name
                      : supplier?.company_name
                    : supplierDetail?.name
                    ? supplierDetail?.name
                    : supplier?.name}
                </Typography>
                <Typography color="textSecondary" variant="h6">
                  {supplierDetail?.terms
                    ? supplierDetail?.terms
                    : supplier?.terms}
                </Typography>
              </Box>
            </Grid>
            {canEditSupplier && (
              <Grid item lg={4} md={4} xs={4}>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    onClick={() => setShowEdit(!showEdit)}
                    variant="contained"
                  >
                    {showEdit ? 'Hide Edit Fields' : 'Edit'}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
          {canEditSupplier && (
            <SupplierDialogForm
              onDeletePress={onDeleteSupplier}
              isVisible={showEdit}
              onSavePress={onSaveDetailPress}
              handleClose={() => setShowEdit(false)}
              supplier={supplierDetail || supplier}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupplierDetailView;
